import type Stripe from 'stripe';

import { supabase } from "@/lib/supabase";

// Export commonly used Stripe types - using union with base Stripe type
export type StripePrice = Stripe.Price;

// Database-first types (for subscription data stored in our DB) 
interface SubscriptionDataEntry {
    id: string;
    customer_id: string;
    status: string;
    plan_name: string;
    [key: string]: string | number | boolean | null;
}

// API Response types for edge functions (using proper Stripe types)
interface GetProductsResponse {
    products: Stripe.Product[];
    error?: string;
}

interface GetSubscriptionResponse {
    subscription: SubscriptionDataEntry | null;
    error?: string;
}

// Response interfaces for our service functions
interface FetchPricesResult {
    prices: Stripe.Price[];
    error: string | null;
    isLoading: boolean;
}

interface FetchProductsResult {
    products: Stripe.Product[];
    error: string | null;
    isLoading: boolean;
}

interface FetchCustomersResult {
    customers: Stripe.Customer[];
    error: string | null;
    isLoading: boolean;
}

interface FetchAccountsResult {
    accounts: Stripe.Account[];
    error: string | null;
    isLoading: boolean;
}

interface FetchSubscriptionsResult {
    subscriptions: Stripe.Subscription[];
    error: string | null;
    isLoading: boolean;
}

interface FetchPaymentIntentsResult {
    paymentIntents: Stripe.PaymentIntent[];
    error: string | null;
    isLoading: boolean;
}

interface FetchCheckoutSessionsResult {
    checkoutSessions: Stripe.Checkout.Session[];
    error: string | null;
    isLoading: boolean;
}

// Fetch prices from Stripe via Supabase Edge Function
export async function fetchPrices(): Promise<FetchPricesResult> {
    try {
        // First try to get prices from the get-products-public edge function
        const response = await supabase.functions.invoke('get-products-public', {
            body: {}
        });
        if (response.error) {
            const errorMessage = typeof response.error === 'object' && response.error !== null && 'message' in response.error 
                ? String((response.error as { message: string }).message) 
                : 'Failed to fetch products';
            throw new Error(errorMessage);
        }

        const data = response.data as Record<string, string | number | boolean | null | object> | null;

        if (!data || typeof data !== 'object') {
            console.warn('[paymentsDb] get-products edge function returned invalid data');
            return { prices: [], error: 'No prices returned from Stripe', isLoading: false };
        }

        const prices: Stripe.Price[] = [];

        // Extract prices from top-level `prices` array (current edge function format)
        if ('prices' in data && Array.isArray(data.prices)) {
            (data.prices as Stripe.Price[]).forEach((priceData: Stripe.Price) => {
                prices.push(priceData);
            });
        }

        // Also check for prices nested inside products (legacy format)
        if (prices.length === 0 && 'products' in data && Array.isArray(data.products)) {
            const productsResponse = data as unknown as GetProductsResponse;
            productsResponse.products.forEach((product: Stripe.Product) => {
                if ('prices' in product && product.prices && Array.isArray(product.prices)) {
                    (product.prices as Stripe.Price[]).forEach((priceData: Stripe.Price) => {
                        prices.push(priceData);
                    });
                }
            });
        }

        // Normalize prices: map `attrs` to `recurring` if `recurring` is missing
        // (the deployed edge function returns price intervals in `attrs` instead of `recurring`)
        const normalizedPrices = prices.map(price => {
            const p = price as Stripe.Price & { attrs?: { interval?: string; interval_count?: number; type?: string } };
            if (!p.recurring && p.attrs?.interval) {
                return {
                    ...p,
                    recurring: {
                        interval: p.attrs.interval as Stripe.Price.Recurring.Interval,
                        interval_count: p.attrs.interval_count ?? 1,
                        aggregate_usage: null,
                        trial_period_days: null,
                        usage_type: 'licensed' as const,
                        meter: null,
                    },
                } as Stripe.Price;
            }
            return price;
        });

        if (normalizedPrices.length > 0) {
            return { prices: normalizedPrices, error: null, isLoading: false };
        }

        console.warn('[paymentsDb] get-products edge function did not return price data');
        return { prices: [], error: 'No prices returned from Stripe', isLoading: false };
    } catch (error) {
        console.error('Error in fetchPrices:', error)
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch prices';
        return { prices: [], error: errorMessage, isLoading: false }
    }
}

// Fetch products from Stripe via Supabase Edge Function
export async function fetchProducts(): Promise<FetchProductsResult> {
    try {
        // Call the get-products-public Supabase Edge Function
        const response = await supabase.functions.invoke('get-products-public', {
            body: {}
        });
        if (response.error) {
            const errorMessage = typeof response.error === 'object' && response.error !== null && 'message' in response.error 
                ? String((response.error as { message: string }).message) 
                : 'Failed to fetch products';
            console.error('Error calling get-products function:', errorMessage);
            return { products: [], error: errorMessage, isLoading: false };
        }

        const data = response.data as Record<string, string | number | boolean | null | object> | null;
        
        // Type guard to ensure data is the expected shape
        if (!data || typeof data !== 'object' || !('products' in data) || !Array.isArray(data.products)) {
            console.warn('[paymentsDb] get-products function returned invalid data structure');
            return { products: [], error: 'Invalid data structure returned from Stripe', isLoading: false };
        }

        // Use Stripe products directly
        const productsResponse = data as unknown as GetProductsResponse;
        const products: Stripe.Product[] = productsResponse.products;

        return { products, error: null, isLoading: false };
    } catch (error) {
        console.error('Error in fetchProducts:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch products';
        return {
            products: [],
            error: errorMessage,
            isLoading: false
        };
    }
}


// Deprecated: Use edge functions instead
export async function fetchCustomers(): Promise<FetchCustomersResult> {
    return { customers: [], error: null, isLoading: false };
}

// Deprecated: Use edge functions instead
export async function fetchAccounts(): Promise<FetchAccountsResult> {
    return { accounts: [], error: null, isLoading: false };
}

// Deprecated: Use checkUserSubscription for individual subscription checks
export async function fetchSubscriptions(): Promise<FetchSubscriptionsResult> {
    return { subscriptions: [], error: null, isLoading: false };
}

// Deprecated: Customer fetching via RPC is deprecated, use edge functions instead
export async function fetchCustomerById(): Promise<Stripe.Customer | null> {
    return null;
}

// Deprecated: Subscription list fetching is not implemented, use checkUserSubscription
export async function fetchCustomerSubscriptions(): Promise<Stripe.Subscription[]> {
    return [];
}

// Check if a user is subscribed
export interface SubscriptionStatusData {
    isSubscribed: boolean;
    subscription?: SubscriptionDataEntry | null;
    priceId?: string;
    planName?: string;
    error?: string | null;
    /** Indicates the subscription needs attention (e.g., past_due status) */
    needsAttention?: boolean;
    /** Warning message when subscription has issues but is still considered active */
    warning?: string | null;
}

export async function checkUserSubscription(userId: string): Promise<SubscriptionStatusData> {
    try {
        // Use the get-subscription edge function instead of direct database queries
        const response = await supabase.functions.invoke('get-subscription', {
            body: {
                entityId: userId,
                entityType: 'user',
            },
        });
        const data = response.data as GetSubscriptionResponse | null;
        const error = response.error as Error | null;

        if (error) {
            console.error('Error fetching subscription:', error);
            return { isSubscribed: false, error: String(error) };
        }

        if (!data?.subscription) {
            return { isSubscribed: false, error: 'No subscription found' };
        }

        // Check if subscription is active
        // TODO: Implement idempotency keys for payment operations to prevent duplicate charges
        // and ensure safe retries. This is especially important for checkout sessions and
        // subscription creation/updates. See: https://stripe.com/docs/api/idempotent_requests
        const subscription = data.subscription;

        // Include past_due status in active check to provide grace period for payment issues
        const isActive = subscription.status === 'active' ||
                         subscription.status === 'trialing' ||
                         subscription.status === 'past_due'; // Give grace period for failed payments

        // Flag subscriptions that need attention (payment failed but still in grace period)
        const needsAttention = subscription.status === 'past_due';
        const warning = needsAttention
            ? 'Your subscription payment is past due. Please update your payment method to avoid service interruption.'
            : null;

        return {
            isSubscribed: isActive,
            subscription,
            planName: String(subscription.plan_name),
            error: null,
            needsAttention,
            warning
        };
    } catch (error) {
        console.error('Error checking user subscription:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to check subscription';
        return { isSubscribed: false, error: errorMessage };
    }
}

// Fetch a specific price by ID from public schema
export async function fetchPriceById(priceId: string): Promise<Stripe.Price | null> {
    try {
        // Use the get-products-public edge function to find the price
        const response = await supabase.functions.invoke('get-products-public', {
            body: {}
        });
        const data = response.data as GetProductsResponse | null;
        const error = response.error as Error | null;

        if (!error && data?.products && Array.isArray(data.products)) {
            // Look through products to find the matching price
            for (const product of data.products) {
                if ('prices' in product && product.prices && Array.isArray(product.prices)) {
                    const price = (product.prices as Stripe.Price[]).find((p: Stripe.Price) => p.id === priceId);
                    if (price) {
                        return price;
                    }
                }
            }
        }

        return null
    } catch (error) {
        console.error('Error fetching price:', error)
        return null
    }
}

// Fetch product details by ID from Stripe via Supabase Edge Function
export async function fetchProductById(productId: string): Promise<Stripe.Product | null> {
    try {
        // First try to get the product from the edge function
        const response = await supabase.functions.invoke('get-products-public', {
            body: {}
        });
        const data = response.data as GetProductsResponse | null;
        const error = response.error as Error | null;

        if (!error && data?.products && Array.isArray(data.products)) {
            // Find the product with the matching ID
            const stripeProduct = data.products.find((p: Stripe.Product) => p.id === productId);
            
            if (stripeProduct) {
                return stripeProduct;
            }
        }

        return null
    } catch (error) {
        console.error('Error fetching product:', error)
        return null
    }
}

// Deprecated wrappers — kept for backwards compatibility. Use edge functions instead.

export async function fetchStripeCustomers(): Promise<FetchCustomersResult> {
    return { customers: [], error: null, isLoading: false };
}

export async function fetchStripeCustomerById(): Promise<Stripe.Customer | null> {
    return null;
}

export async function fetchStripeSubscriptions(): Promise<FetchSubscriptionsResult> {
    return { subscriptions: [], error: null, isLoading: false };
}

export async function fetchStripeCustomerSubscriptions(): Promise<Stripe.Subscription[]> {
    return [];
}

export async function fetchStripePaymentIntents(): Promise<FetchPaymentIntentsResult> {
    return { paymentIntents: [], error: null, isLoading: false };
}

export async function fetchStripeCustomerPaymentIntents(): Promise<Stripe.PaymentIntent[]> {
    return [];
}

export async function fetchStripeCheckoutSessions(): Promise<FetchCheckoutSessionsResult> {
    return { checkoutSessions: [], error: null, isLoading: false };
}

export async function fetchStripeCustomerCheckoutSessions(): Promise<Stripe.Checkout.Session[]> {
    return [];
}

export async function fetchStripeAccounts(): Promise<FetchAccountsResult> {
    return { accounts: [], error: null, isLoading: false };
}
